import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Divider,
  TextField,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useDevMailFactory from '../../hooks/useDevMailFactory';
import { useAuth } from '../../context/authContext';

const isDevEnvironment = Boolean(import.meta.env?.DEV) || import.meta.env?.VITE_IS_DEV === 'true';

const MODE_OPTIONS = [
  { value: 'program', label: 'Programmierte Mail (Inbox)' },
  { value: 'reply', label: 'Antwort auf Thread (Inbox)' },
];

const safeStringify = value => {
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return '';
  }
};

export default function DevMailFactoryView() {
  const authCtx = useAuth();
  const {
    getBookingWidgets,
    getKnowledgeBaseArticles,
    getScenarios,
    getRecentBookings,
    getProgramDefaults,
    getReplyableThreads,
    getReplyDefaults,
    createProgramMail,
  } = useDevMailFactory();

  const getBookingWidgetsRef = useRef(getBookingWidgets);
  const getKnowledgeBaseArticlesRef = useRef(getKnowledgeBaseArticles);
  const getScenariosRef = useRef(getScenarios);
  const getRecentBookingsRef = useRef(getRecentBookings);
  const getProgramDefaultsRef = useRef(getProgramDefaults);
  const getReplyableThreadsRef = useRef(getReplyableThreads);
  const getReplyDefaultsRef = useRef(getReplyDefaults);
  const createProgramMailRef = useRef(createProgramMail);

  useEffect(() => {
    getBookingWidgetsRef.current = getBookingWidgets;
  }, [getBookingWidgets]);

  useEffect(() => {
    getKnowledgeBaseArticlesRef.current = getKnowledgeBaseArticles;
  }, [getKnowledgeBaseArticles]);

  useEffect(() => {
    getScenariosRef.current = getScenarios;
  }, [getScenarios]);

  useEffect(() => {
    getRecentBookingsRef.current = getRecentBookings;
  }, [getRecentBookings]);

  useEffect(() => {
    getProgramDefaultsRef.current = getProgramDefaults;
  }, [getProgramDefaults]);

  useEffect(() => {
    getReplyableThreadsRef.current = getReplyableThreads;
  }, [getReplyableThreads]);

  useEffect(() => {
    getReplyDefaultsRef.current = getReplyDefaults;
  }, [getReplyDefaults]);

  useEffect(() => {
    createProgramMailRef.current = createProgramMail;
  }, [createProgramMail]);

  const [mode, setMode] = useState('program');

  const [scenarios, setScenarios] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [threads, setThreads] = useState([]);
  const [bookingWidgets, setBookingWidgets] = useState([]);
  const [knowledgeBaseArticles, setKnowledgeBaseArticles] = useState([]);

  const [selectedScenarioKey, setSelectedScenarioKey] = useState('');
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [selectedThreadNumber, setSelectedThreadNumber] = useState('');
  const [selectedBookingWidgetId, setSelectedBookingWidgetId] = useState('');
  const [selectedKnowledgeBaseArticleId, setSelectedKnowledgeBaseArticleId] = useState('');

  const [defaults, setDefaults] = useState(null);
  const [mailJsonText, setMailJsonText] = useState('');

  const [isLoadingLists, setIsLoadingLists] = useState(false);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const loadLists = useCallback(async () => {
    setIsLoadingLists(true);
    try {
      const [scenarioRes, bookingRes, threadRes, widgetRes, kbRes] = await Promise.allSettled([
        getScenariosRef.current(),
        getRecentBookingsRef.current(),
        getReplyableThreadsRef.current(),
        getBookingWidgetsRef.current(),
        getKnowledgeBaseArticlesRef.current({ status: 'published', limit: 200, skip: 0 }),
      ]);

      if (scenarioRes.status !== 'fulfilled') {
        throw scenarioRes.reason;
      }
      if (bookingRes.status !== 'fulfilled') {
        throw bookingRes.reason;
      }
      if (threadRes.status !== 'fulfilled') {
        throw threadRes.reason;
      }

      const scenarioList = scenarioRes.value;
      const bookingList = bookingRes.value;
      const threadList = threadRes.value;
      const widgetList = widgetRes.status === 'fulfilled' ? widgetRes.value : [];
      const kbArticles = kbRes.status === 'fulfilled' ? kbRes.value : [];

      if (widgetRes.status === 'rejected') {
        toast.error('Fehler beim Laden der Widgets.');
      }
      if (kbRes.status === 'rejected') {
        toast.error('Fehler beim Laden der Wissensdatenbank-Artikel.');
      }

      setScenarios(scenarioList);
      setBookings(bookingList);
      setThreads(threadList);
      setBookingWidgets(widgetList);
      setKnowledgeBaseArticles(kbArticles);
      setSelectedScenarioKey(prev => prev || scenarioList?.[0]?.key || '');
      setSelectedBookingWidgetId(prev => prev || widgetList?.[0]?._id || '');
      setSelectedKnowledgeBaseArticleId(prev => prev || kbArticles?.[0]?._id || '');
    } catch (error) {
      toast.error('Fehler beim Laden der Mail-Factory Daten.');
    } finally {
      setIsLoadingLists(false);
    }
  }, []);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  useEffect(() => {
    setDefaults(null);
    setMailJsonText('');
  }, [mode]);

  const isNewBookingScenario = selectedScenarioKey === 'new_booking';
  const isKnowledgeBaseScenario = selectedScenarioKey === 'knowledge_base';

  useEffect(() => {
    if (isNewBookingScenario && !selectedBookingWidgetId) {
      setSelectedBookingWidgetId(bookingWidgets?.[0]?._id || '');
    }
    if (isKnowledgeBaseScenario && !selectedKnowledgeBaseArticleId) {
      setSelectedKnowledgeBaseArticleId(knowledgeBaseArticles?.[0]?._id || '');
    }
  }, [
    bookingWidgets,
    isKnowledgeBaseScenario,
    isNewBookingScenario,
    knowledgeBaseArticles,
    selectedBookingWidgetId,
    selectedKnowledgeBaseArticleId,
  ]);

  const handleLoadDefaults = useCallback(async () => {
    setIsLoadingDefaults(true);
    try {
      const response =
        mode === 'program'
          ? await getProgramDefaultsRef.current({
              scenarioKey: selectedScenarioKey || undefined,
              bookingId: selectedBookingId || undefined,
              bookingWidgetId: isNewBookingScenario
                ? selectedBookingWidgetId || undefined
                : undefined,
              knowledgeBaseArticleId: isKnowledgeBaseScenario
                ? selectedKnowledgeBaseArticleId || undefined
                : undefined,
            })
          : await getReplyDefaultsRef.current({
              threadNumber: selectedThreadNumber || undefined,
            });

      setDefaults(response);
      setMailJsonText(safeStringify(response?.mailDraft ?? {}));
    } catch (error) {
      toast.error('Fehler beim Laden der Defaults.');
    } finally {
      setIsLoadingDefaults(false);
    }
  }, [
    isKnowledgeBaseScenario,
    isNewBookingScenario,
    mode,
    selectedBookingId,
    selectedBookingWidgetId,
    selectedKnowledgeBaseArticleId,
    selectedScenarioKey,
    selectedThreadNumber,
  ]);

  const parsedMailJson = useMemo(() => {
    if (!mailJsonText) return { value: null, error: null };
    try {
      return { value: JSON.parse(mailJsonText), error: null };
    } catch (error) {
      return { value: null, error: 'Ungültiges JSON.' };
    }
  }, [mailJsonText]);

  const handleCreateMail = useCallback(async () => {
    if (!parsedMailJson.value) {
      toast.info('Bitte gültiges JSON als Mail-Payload angeben.');
      return;
    }

    setIsSending(true);
    try {
      const response = await createProgramMailRef.current({ mail: parsedMailJson.value });
      const createdId = response?.mail?.id;
      toast.success(createdId ? `Mail erstellt: ${createdId}` : 'Mail erstellt.');
    } catch (error) {
      toast.error('Fehler beim Erstellen der Mail.');
    } finally {
      setIsSending(false);
    }
  }, [parsedMailJson.value]);

  const scenarioInfo = defaults?.scenario ?? null;
  const selectedBooking = defaults?.booking ?? null;
  const threadMessages = defaults?.thread ?? [];

  const canLoadDefaults =
    mode === 'program'
      ? Boolean(selectedScenarioKey) &&
        (!isNewBookingScenario || Boolean(selectedBookingWidgetId)) &&
        (!isKnowledgeBaseScenario || Boolean(selectedKnowledgeBaseArticleId))
      : Boolean(selectedThreadNumber && String(selectedThreadNumber).trim().length > 0);

  if (!isDevEnvironment) {
    return <Navigate to='/welcome' replace />;
  }

  if (!authCtx.checkRight('dev:tool:mailfactory')) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <Box padding={3}>
      <Paper sx={{ padding: 3, marginBottom: 3 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant='subtitle2' gutterBottom>
              Modus auswählen
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={mode}
              onChange={(_, value) => value && setMode(value)}
              size='small'
            >
              {MODE_OPTIONS.map(option => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {isLoadingLists && (
            <Box display='flex' justifyContent='center' paddingY={2}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!isLoadingLists && mode === 'program' && (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel id='scenario-select-label'>Szenario</InputLabel>
                <Select
                  labelId='scenario-select-label'
                  label='Szenario'
                  value={selectedScenarioKey}
                  onChange={event => setSelectedScenarioKey(event.target.value)}
                >
                  {scenarios.map(s => (
                    <MenuItem key={s.key} value={s.key}>
                      {s.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {isNewBookingScenario && (
                <FormControl fullWidth>
                  <InputLabel id='booking-widget-select-label'>Widget</InputLabel>
                  <Select
                    labelId='booking-widget-select-label'
                    label='Widget'
                    value={selectedBookingWidgetId}
                    onChange={event => setSelectedBookingWidgetId(event.target.value)}
                  >
                    {bookingWidgets.length === 0 && (
                      <MenuItem value='' disabled>
                        Keine Widgets verfügbar
                      </MenuItem>
                    )}
                    {bookingWidgets.map(w => (
                      <MenuItem key={w._id} value={w._id}>
                        {w.name || '—'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {isKnowledgeBaseScenario && (
                <FormControl fullWidth>
                  <InputLabel id='kb-article-select-label'>Wissensdatenbank-Artikel</InputLabel>
                  <Select
                    labelId='kb-article-select-label'
                    label='Wissensdatenbank-Artikel'
                    value={selectedKnowledgeBaseArticleId}
                    onChange={event => setSelectedKnowledgeBaseArticleId(event.target.value)}
                  >
                    {knowledgeBaseArticles.length === 0 && (
                      <MenuItem value='' disabled>
                        Keine Artikel verfügbar
                      </MenuItem>
                    )}
                    {knowledgeBaseArticles.map(a => (
                      <MenuItem key={a._id} value={a._id}>
                        {a.title || '—'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {!isNewBookingScenario && !isKnowledgeBaseScenario && (
                <FormControl fullWidth>
                  <InputLabel id='booking-select-label'>Buchung (optional)</InputLabel>
                  <Select
                    labelId='booking-select-label'
                    label='Buchung (optional)'
                    value={selectedBookingId}
                    onChange={event => setSelectedBookingId(event.target.value)}
                  >
                    <MenuItem value=''>—</MenuItem>
                    {bookings.map(b => (
                      <MenuItem key={b.id} value={b.id}>
                        {b.bookingNumber || '—'} · {b.contact?.mail || '—'} ·{' '}
                        {b.startTime ? dayjs(b.startTime).format('DD.MM.YYYY HH:mm') : '—'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>
          )}

          {!isLoadingLists && mode === 'reply' && (
            <FormControl fullWidth>
              <InputLabel id='thread-select-label'>Thread</InputLabel>
              <Select
                labelId='thread-select-label'
                label='Thread'
                value={selectedThreadNumber}
                onChange={event => setSelectedThreadNumber(event.target.value)}
              >
                {threads.map(t => (
                  <MenuItem key={t.threadNumber} value={t.threadNumber}>
                    {t.threadNumber} · {t.subject || '—'} ·{' '}
                    {t.lastMessageAt ? dayjs(t.lastMessageAt).format('DD.MM.YYYY HH:mm') : '—'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box>
            <Button
              variant='contained'
              disabled={!canLoadDefaults || isLoadingDefaults}
              onClick={handleLoadDefaults}
            >
              {isLoadingDefaults ? <CircularProgress size={20} /> : 'Mail erstellen'}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ padding: 3 }}>
        <Stack spacing={2}>
          <Typography variant='h6'>Mail erstellen</Typography>

          <Divider />

          {scenarioInfo && (
            <Box>
              <Typography variant='subtitle2' gutterBottom>
                Szenario
              </Typography>
              <Typography variant='body2'>
                {scenarioInfo.title}
                {scenarioInfo.instructions ? ` — ${scenarioInfo.instructions}` : ''}
              </Typography>
            </Box>
          )}

          {selectedBooking && (
            <Box>
              <Typography variant='subtitle2' gutterBottom>
                Buchung
              </Typography>
              <Typography variant='body2'>
                {(selectedBooking.bookingNumber || '—') + ' · '}
                {(selectedBooking.contact?.mail || '—') + ' · '}
                {selectedBooking.startTime
                  ? dayjs(selectedBooking.startTime).format('DD.MM.YYYY HH:mm')
                  : '—'}
              </Typography>
            </Box>
          )}

          {mode === 'reply' && threadMessages.length > 0 && (
            <Box>
              <Typography variant='subtitle2' gutterBottom>
                Thread (letzte Nachrichten)
              </Typography>
              <List dense>
                {threadMessages.slice(0, 8).map(message => (
                  <ListItem key={message.id} alignItems='flex-start'>
                    <ListItemText
                      primary={`${message.direction === 'inbound' ? 'Inbound' : 'Outbound'} · ${
                        message.from || '—'
                      } · ${message.receivedAt ? dayjs(message.receivedAt).format('DD.MM.YYYY HH:mm') : '—'}`}
                      secondary={message.textBody || '—'}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <TextField
            label='Mail Payload (JSON)'
            value={mailJsonText}
            onChange={event => setMailJsonText(event.target.value)}
            minRows={12}
            multiline
            fullWidth
            error={Boolean(parsedMailJson.error)}
            helperText={
              parsedMailJson.error || 'Die JSON-Struktur wird 1:1 an das Backend gesendet.'
            }
          />

          <Box>
            <Button
              variant='contained'
              disabled={!parsedMailJson.value || isSending}
              onClick={handleCreateMail}
            >
              {isSending ? <CircularProgress size={20} /> : 'Mail senden'}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
